import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
  sm: {
    boxSize: "16px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    padding: tokens.globals["spacing-sm"],
    iconSize: "12px",
    viewBox: "0 0 12 12",
    path: "M2 6L4.5 8.5L10 3",
  },
  md: {
    boxSize: "20px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    padding: tokens.globals["spacing-md"],
    iconSize: "16px",
    viewBox: "0 0 16 16",
    path: "M3 8L6.5 11.5L13 5",
  },
  lg: {
    boxSize: "24px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    padding: tokens.globals["spacing-md"],
    iconSize: "20px",
    viewBox: "0 0 20 20",
    path: "M4 10L8.5 14.5L16 6",
  },
};

const defaultFocusVisibleState = {
  outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
  boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
};

const errorState = {
  outline: `1px solid ${tokens.colors.ui.destructive}`,
  boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const variantConfig = {
  primary: {
    background: tokens.colors.ui.primary,
    color: tokens.colors.ui["primary-foreground"],
    hover: {
      background: tokens.colors.ui.primary,
    },
    active: {
      background: tokens.colors.ui.primary,
    },
  },
  secondary: {
    background: tokens.colors.brand.blue["100"],
    color: tokens.colors.brand["aie-blue"]["500"],
    hover: {
      background: tokens.colors.brand.blue["200"],
    },
    active: {
      background: tokens.colors.brand.blue["300"],
    },
  },
  destructive: {
    background: tokens.colors.states.alert["50"],
    color: tokens.colors.ui.destructive,
    hover: {
      background: tokens.colors.states.alert["100"],
    },
    active: {
      background: tokens.colors.states.alert["50"],
    },
  },
};

const getVariantStyles = (variant) => {
  const config = variantConfig[variant] || variantConfig.primary;
  const { background, color, hover, active } = config;

  return {
    background,
    color,
    hover,
    active,
  };
};

const StyledHiddenCheckbox = styled.input.attrs({
  type: "checkbox",
})(() => ({
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
}));

const StyledCheckboxBox = styled.span(
  ({ $size = "lg", $variant = "primary", $checked = false, $indeterminate = false }) => {
    const { boxSize, fontSize, lineHeight, padding, iconSize } = sizes[$size];
    const variantStyles = getVariantStyles($variant);
    const isSelected = $checked || $indeterminate;

    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      width: boxSize,
      height: boxSize,
      borderRadius: "4px",
      border: `1px solid ${tokens.colors.ui.border}`,
      transition: "all 0.2s ease",
      fontFamily: tokens.typography.fontFamilies["albert-sans"],
      fontWeight: tokens.typography.fontWeights.bold,
      fontSize,
      lineHeight,
      padding,
      background: isSelected ? variantStyles.background : tokens.colors.ui.background,
      color: isSelected ? variantStyles.color : "transparent",
      borderColor: isSelected ? variantStyles.background : tokens.colors.ui.border,
      position: "relative",
      boxShadow: "0px 0px 6px rgba(55, 59, 64, 0.05)",

      "&:hover": variantStyles.hover,
      "&:active": variantStyles.active,

      "& svg": {
        width: iconSize,
        height: iconSize,
        display: "block",
        flexShrink: 0,
      },
    };
  }
);

const StyledIndeterminateLine = styled.div(({ $size = "lg" }) => ({
  width: sizes[$size].iconSize,
  height: "1.5px",
  background: "currentColor",
  borderRadius: "1px",
  flexShrink: 0,
}));

const StyledCheckboxWrapper = styled.label(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: tokens.globals["spacing-sm"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontSize: tokens.typography.fontSize["lg-14px"],
  fontWeight: tokens.typography.fontWeights.medium,
  color: tokens.colors.ui.foreground,
  cursor: "pointer",

  "&:has(input:disabled)": {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  "input:focus-visible + span": defaultFocusVisibleState,

  'input[data-error="true"] + span': errorState,
}));

/**
 * A checkbox component with support for multiple variants, sizes, and states.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.size="lg"] - Size variant: "sm", "md", or "lg"
 * @param {string} [props.variant="primary"] - Visual variant: "primary", "secondary", or "destructive"
 * @param {boolean} [props.checked=false] - Whether the checkbox is checked
 * @param {boolean} [props.indeterminate=false] - Whether the checkbox is in indeterminate state
 * @param {boolean} [props.disabled=false] - If true, the checkbox is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.label] - Label text to display next to the checkbox
 * @param {function} [props.onChange] - Callback fired when the checkbox state changes
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered checkbox component
 */
const Checkbox = forwardRef(
  (
    {
      size = "lg",
      variant = "primary",
      checked = false,
      indeterminate = false,
      disabled = false,
      error = false,
      label,
      onChange,
      ...rest
    },
    ref
  ) => {
    const internalRef = useRef(null);

    useImperativeHandle(ref, () => internalRef.current);

    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const { viewBox, path } = sizes[size];

    return (
      <StyledCheckboxWrapper>
        <StyledHiddenCheckbox
          ref={internalRef}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          data-error={error ? "true" : undefined}
          {...rest}
        />

        <StyledCheckboxBox
          $size={size}
          $variant={variant}
          $checked={checked}
          $indeterminate={indeterminate}
        >
          {checked && (
            <svg
              viewBox={viewBox}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d={path}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}

          {!checked && indeterminate && (
            <StyledIndeterminateLine $size={size} />
          )}
        </StyledCheckboxBox>

        {label && <span>{label}</span>}
      </StyledCheckboxWrapper>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;